<template>
  <idf-carousel
    v-if="articleThumbnails && articles && articles.length"
    ref="theCarousel"
    v-observe-visibility="{
      callback: carouselVisibilityChanged,
      throttle: 1000,
      intersection: { threshold: 0.75 },
      once: true,
    }"
    :background="(articleThumbnails.backgroundColor && { color: articleThumbnails.backgroundColor }) || undefined"
    :style="itemMargin && `--idf-carousel-items-gap: ${itemMargin}`"
    :value="
      ((articleThumbnails.title || articleThumbnails.description) && {
        title: articleThumbnails.title,
        description: articleThumbnails.description,
      }) ||
      undefined
    "
    class="carousel-articles"
    @items-visibility-change="itemsVisibilityChanged"
    :item-list-length="articles.length"
  >
    <template v-for="(article, index) in articles" #[index] :key="index">
      <div data-test-article-wrapper @click.capture="onArticleClick(article)">
        <div v-if="isHighlighted(index)" class="carousel-article__highlighted">
          <p-article-thumbnail-highlighted
            :article="convertToHighlightedArticle(article)"
            class="carousel-article__highlighted--inside"
            data-test-article-highlighted
          />
        </div>
        <idf-article-thumbnail
          v-else
          :article="article"
          :truncate-title="truncateTitle"
          :use-spacer="!!article.summary && !!article.header.publishedAt"
          :format-date="formatDate"
        />
      </div>
    </template>
    <template v-if="articleThumbnails.buttonLabel" #footer>
      <div class="carousel-article__footer">
        <idf-component-link :url="articleThumbnails.buttonUrl" @click.capture="onCtaClicked">
          <adn-button kind="secondary">
            {{ articleThumbnails.buttonLabel }}
          </adn-button>
        </idf-component-link>
      </div>
    </template>
  </idf-carousel>
</template>

<script lang="ts">
import { defineComponent, onBeforeMount, onMounted, type PropType, type Ref, ref, toRefs } from 'vue'
import AdnButton from '@b2ag/design-system/src/components/AdnButton/AdnButton.vue'
import IdfCarousel from '@b2ag/design-system/src/components/IdfCarousel/IdfCarousel.vue'
import { formatDate } from '@b2ag/l10n'
import { articleUseCase } from '../../../dependency-injection'
import type { ArticleThumbnails } from '../../../domain/ArticleThumbnails'
import type { Article } from '../../../domain/Article'
import IdfArticleThumbnail from '@b2ag/design-system/src/components/IdfArticleThumbnail/IdfArticleThumbnail.vue'
import PArticleThumbnailHighlighted from '../../presenters/PArticleThumbnailHighlighted.vue'
import IdfComponentLink from '@b2ag/design-system/src/components/IdfComponentLink/IdfComponentLink.vue'
import { useEditorialsReactions } from '@b2ag/marketing/src/application/composables/useEditorialsReactions'
import { getConfig } from '@b2ag/micro-front-tools'
import { trackEvent } from '@b2ag/analytics/src/useAnalytics'
import { i18n } from '@b2ag/locale'

interface IdfCarouselExpose {
  visibleItemsIdx: number[]
}

export default defineComponent({
  name: 'CCarouselArticle',
  components: { AdnButton, IdfCarousel, IdfArticleThumbnail, IdfComponentLink, PArticleThumbnailHighlighted },
  props: {
    articleThumbnails: {
      type: Object as PropType<ArticleThumbnails>,
      required: true,
    },
    truncateTitle: { type: Number, default: -1 },
    itemMargin: String,
    blockOrigin: String,
    excludeSlug: String,
    isAuthenticated: { type: Boolean, default: false },
    partnerId: { type: Number, default: undefined },
  },
  setup(props) {
    const { isAuthenticated, partnerId } = toRefs(props)
    const theCarousel: Ref<IdfCarouselExpose | null> = ref(null)
    let carouselDisplayed = false
    let currentDisplayed: string[] = []
    const articles: Ref<Array<Article> | null> = ref(null)
    const convertToHighlightedArticle = (article) => {
      return {
        title: article.title,
        tags: article.header.richBoxTag,
        url: `${getConfig('FRONT_URL')}/article/${article.slug}`,
        backgroundImage: article.header.image || article.header.imageUrl,
        buttonLabel: i18n._("Lire l'article"),
      }
    }

    const isHighlighted = (index) => {
      return props.articleThumbnails?.highlighted && index === 0
    }

    function onCtaClicked() {
      const payload = { content_title: props.articleThumbnails?.title }
      trackEvent('article_thumbnail_click_cta', payload)
    }

    onBeforeMount(() => {
      articleUseCase
        .getArticles(props.articleThumbnails, isAuthenticated.value, partnerId.value)
        .then((articleList) => {
          articles.value = articleList.filter((article) => article.slug !== props.excludeSlug)
        })
    })

    const editorialsReactions = useEditorialsReactions().inject()

    onMounted(() => {
      editorialsReactions.pushArticlesAnchorMounted(props.articleThumbnails?.anchorLabel)
    })

    function getVisibileItemNames(itemListIdx: number[]) {
      const content_title: string[] = []
      itemListIdx?.forEach((idx) => {
        const curTitle = articles.value?.at(idx)?.title
        if (curTitle) content_title.push(curTitle)
      })
      return content_title
    }

    function isSameList(prevList, nextList) {
      return prevList.length === nextList.length && prevList.every((item) => nextList.includes(item))
    }

    function visibilityChanged(isVisible) {
      if (isVisible) {
        const currentVisible = getVisibileItemNames(theCarousel.value?.visibleItemsIdx ?? [])
        const okToSend = !isSameList(currentDisplayed, currentVisible)
        currentDisplayed = currentVisible
        if (okToSend) {
          const payLoad = {
            content_title: currentVisible,
            bloc_title: props.blockOrigin,
          }
          trackEvent('view_article_thumbnail', payLoad)
        }
      }
    }

    function carouselVisibilityChanged(event) {
      carouselDisplayed = true
      visibilityChanged(event)
    }

    function itemsVisibilityChanged() {
      if (carouselDisplayed && theCarousel.value?.visibleItemsIdx?.length) visibilityChanged(true)
    }

    return {
      articles,
      convertToHighlightedArticle,
      isHighlighted,
      onCtaClicked,
      onArticleClick: (article: Article) => {
        const payLoad = {
          content_title: article.title,
          content_slug: article.slug,
          bloc_title: props.blockOrigin,
        }
        trackEvent(`click_article_thumbnail`, payLoad)
      },
      carouselVisibilityChanged,
      itemsVisibilityChanged,
      formatDate,
      theCarousel,
      visibilityChanged,
    }
  },
})
</script>

<style lang="scss">
.carousel-article__highlighted--inside {
  height: 100%;
  width: 100%;
}

.carousel-article__highlighted {
  height: var(--highlighted-article-lg-height, 36rem);
  width: var(--highlighted-article-lg-width, 46rem);

  @media (max-width: $breakpoint-md) {
    height: var(--highlighted-article-sm-height, 26rem);
    width: var(--highlighted-article-sm-width, 26rem);
  }
}

.carousel-article__footer {
  display: flex;
  justify-content: center;

  .adn-button {
    cursor: pointer;
  }
}
</style>
